import React, { useEffect, useId, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Info, Landmark } from 'lucide-react';
import { getBankAccounts } from '../../api/bankAccounts';
import { useBanks } from '../../hooks/useBanks';
import type { BankAccount } from '../../types/BankAccount';
import { t } from '../../i18n/t';
import './FundAccountsPanel.css';
import { showErrorToast, describeError } from '../../components/ErrorToast';

interface FundAccountsPanelProps {
  fundId: number;
  fundName: string;
}

// Read-only view of the bank accounts that belong to a single fund.
// Add/remove of accounts happens in /bank-accounts — this panel only displays
// the current set and ships a CTA that pre-populates that screen's search box
// with the fund name.
export function FundAccountsPanel({ fundId, fundName }: FundAccountsPanelProps) {
  const navigate = useNavigate();
  const titleId = useId();

  const { data: banks } = useBanks();

  const [accounts, setAccounts] = useState<BankAccount[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Re-fetch whenever the fund changes. No backend filter — fetch all and
  // filter client-side (existing pattern; BankAccounts is a small list).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getBankAccounts()
      .then((rows) => {
        if (cancelled) return;
        setAccounts(rows.filter((a) => a.fundId === fundId));
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        showErrorToast(err, { title: t('errorToast.titleLoad') });
        setError(describeError(err).body);
        setAccounts(null);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fundId]);

  // bankId → bankName. Orphan ids (deleted bank) fall back to `#<id>` so the
  // row stays visible instead of disappearing.
  const bankNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const b of banks ?? []) map.set(b.bankId, b.bankName);
    return map;
  }, [banks]);

  const handleManageClick = () => {
    navigate(`/settings/bank-accounts?search=${encodeURIComponent(fundName)}`);
  };

  return (
    <section className="lop-fap" aria-labelledby={titleId}>
      <div className="lop-fap__header">
        <h2 id={titleId} className="lop-fap__title">
          {t('fundAccounts.panelTitle')}
        </h2>
        <button
          type="button"
          className="lop-fap__cta"
          onClick={handleManageClick}
          aria-label={t('fundAccounts.manageAccountsAriaLabel', { fundName })}
        >
          <ExternalLink size={16} aria-hidden="true" />
          <span>{t('fundAccounts.manageAccountsCta')}</span>
        </button>
      </div>

      <div className="lop-fap__note">
        <Info size={14} aria-hidden="true" />
        <span>{t('fundAccounts.managedElsewhereNote')}</span>
      </div>

      {loading && (
        <div className="lop-fap__loading">{t('common.loading')}</div>
      )}

      {error && (
        <div className="lop-fap__error" role="alert">
          {t('fundAccounts.loadError', { message: error })}
        </div>
      )}

      {!loading && !error && accounts && accounts.length === 0 && (
        <div className="lop-fap__empty" role="status">
          <Landmark
            size={28}
            className="lop-fap__empty-icon"
            aria-hidden="true"
          />
          <div className="lop-fap__empty-headline">
            {t('fundAccounts.emptyHeadline')}
          </div>
          <div className="lop-fap__empty-helper">
            {t('fundAccounts.emptyHelper')}
          </div>
        </div>
      )}

      {!loading && !error && accounts && accounts.length > 0 && (
        <div className="lop-fap__table-wrap">
          <table className="lop-fap__table">
            <thead>
              <tr>
                <th scope="col">{t('fundAccounts.columnBank')}</th>
                <th scope="col">{t('fundAccounts.columnBranch')}</th>
                <th scope="col">{t('fundAccounts.columnAcc')}</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((a) => (
                <tr key={a.acc}>
                  <td>
                    {bankNameById.get(a.bankId) ?? `#${a.bankId}`}
                  </td>
                  <td className="lop-fap__num" dir="ltr">
                    {a.branch == null ? '' : String(a.branch)}
                  </td>
                  <td className="lop-fap__num" dir="ltr">
                    {a.acc}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
